import { Button } from '../components/ui/button'
// @ts-strict-ignore
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { axiosGet } from '../util/api'
import { ControlGroup, InputGroup } from '@blueprintjs/core'
import { Callout } from '../components/ui/callout'
import { Layout } from '../components/Layout'
import { EmptyState } from '../components/ui/empty-state'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { Toasts } from '../components/toaster/Toast'
import { percentage } from '../util/formatters'
import type { State } from '../reducers'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../components/table2/AgGridTable'
import type { CellProps } from '../components/table2/ad_grid_columns'
import { TableStore } from '../components/table2/table_store'
import { userStore } from '../stores/user_store'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { observer } from 'mobx-react-lite'
import type { EntityId } from '../types/constants'

type RankedKeyword = {
    keyword: string
    searchTermImpressionRank: number
    searchTermImpressionShare: number
}

type RankedKeywordsProps = {
    loading: boolean
    ranked: RankedKeyword[]
}

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

const SearchTermImpressionShareCellRenderer = ({ data }: CellProps<RankedKeyword>): ReactNode =>
    percentage(data.searchTermImpressionShare, 2)

export const KeywordRecommendations = ({ nested = false }): JSX.Element => {
    const endpoint = 'keyword-recommendations'
    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const [asin, setAsin] = useState('')
    const [results, setResults] = useState(null)
    const [loading, setLoading] = useState(false)
    const [valid, setValid] = useState(false)

    const { ranked, unranked } = useMemo(() => {
        if (!results) {
            return {
                ranked: [],
                unranked: [],
            }
        }

        return {
            ranked: results.filter((i) => i.searchTermImpressionRank != null),
            unranked: results.filter((i) => i.searchTermImpressionRank == null),
        }
    }, [results])

    const load = () => {
        const trimmedAsin = asin.trim()

        if (!profile || trimmedAsin == '') {
            return Promise.resolve()
        }

        if (!isAsinValid(trimmedAsin)) {
            Toasts.error('Invalid ASIN')
            return Promise.resolve()
        }

        const p = {
            asins: [trimmedAsin.trim()],
        }

        setValid(false)
        setLoading(true)
        return axiosGet(endpoint, p)
            .then((res) => {
                setLoading(false)
                setValid(true)
                setResults(res.data.keywordTargetList)
            })
            .catch((err) => {
                setLoading(false)
                console.log('err', err)
            })
    }

    const breadcrumbs = [{ text: 'Tools' }, { href: '', text: 'Keyword Recommendations' }]

    return (
        <Layout breadcrumbs={breadcrumbs} nested={nested}>
            <Callout className="mb-4" intent="primary" title="Beta Feature" icon="info-sign">
                <p className="max-w-3xl">
                    Keyword Recommendations are a beta feature that is still rough around the edges.
                    We will be continuing to add additional features to make this tool more
                    powerful, easy to integrate into your ads, and overall better!
                </p>
            </Callout>
            <div className="mb-2">
                Enter in one of your ASINs to see impression rank, impression share, and Amazon
                recommended keywords.
            </div>
            <div className="mb-4">
                <ControlGroup>
                    <InputGroup
                        type="text"
                        placeholder="ASIN"
                        value={asin}
                        onChange={(e) => setAsin(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key == 'Enter') {
                                load()
                            }
                        }}
                    />
                    <Button loading={loading} intent="primary" text="Load" onClick={() => load()} />
                </ControlGroup>
            </div>

            {valid && (
                <>
                    <RankedKeywords loading={loading} ranked={ranked} />
                    <RecommendedKeywords loading={loading} unranked={unranked} />
                </>
            )}
        </Layout>
    )
}

const RankedKeywords = observer(({ loading, ranked }: RankedKeywordsProps) => {
    const [store] = useState<TableStore<RankedKeyword>>(
        new TableStore(userStore, 'KeywordRecommendations'),
    )
    const savedFiltersStore = useSavedFiltersStore()

    const loadRanked = (params) => {
        console.log('load ranked with', params)

        const sort = (items, key, dir) => {
            if (dir === 'asc') {
                items.sort((a, b) => {
                    if (a[key] > b[key]) {
                        return 1
                    } else if (a[key] < b[key]) {
                        return -1
                    } else {
                        return 0
                    }
                })
            } else if (dir === 'desc') {
                items.sort((a, b) => {
                    if (a[key] > b[key]) {
                        return -1
                    } else if (a[key] < b[key]) {
                        return 1
                    } else {
                        return 0
                    }
                })
            }
        }

        let v = [...ranked]

        if (params.sort) {
            const dir = params.sort[0] === '-' ? 'desc' : 'asc'
            const key = dir == 'desc' ? params.sort.substring(1) : params.sort
            sort(v, key, dir)
        }

        if (params.query) {
            v = v.filter((i) => i.keyword.toLowerCase().indexOf(params.query.toLowerCase()) !== -1)
        }

        return Promise.resolve({
            data: {
                data: v,
                total: v.length,
                from: 1,
                to: v.length,
            },
        })
    }

    const rankedTableHooks = useTableHooksFromStore(loadRanked, {}, true, store)

    useEffect(() => {
        rankedTableHooks.reload()
    }, [ranked])

    const columns = useMemo<AgGridColumnDef<RankedKeyword>[]>(() => {
        const columns: AgGridColumnDef<RankedKeyword>[] = [
            {
                field: 'keyword',
                headerName: 'Keyword',
                headerComponent: agGridComponent(DummyHeader),
            },
            {
                field: 'searchTermImpressionRank',
                headerName: 'Impression Rank',
                headerComponent: agGridComponent(DummyHeader),
            },
            {
                field: 'searchTermImpressionShare',
                headerName: 'Impression Share',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(SearchTermImpressionShareCellRenderer),
            },
        ]

        return columns
    }, [])

    if (loading) {
        return null
    }

    return ranked.length > 0 ? (
        <>
            <div>
                <div className="font-normal text-lg">Ranking Keywords</div>
                <div className="max-w-screen-md">
                    <div className="mt-3">
                        This table is showing search terms that this ASIN has been shown for, as
                        well as your <i>account-level</i> impression rank and share numbers. Both
                        impression rank and impression share are looking at the past 30 days.
                    </div>
                    <div className="mt-1">
                        Impression Rank is a measurement of what rank your account has been showing
                        for that particular search term. An impression rank of 1 means that you have
                        had the most impressions for that search term across the entire Amazon
                        Advertising platform.
                    </div>
                    <div className="mt-1">
                        Impression Share is a the percentage of impressions your account has
                        received for that particular search term. An impression share of 1% means
                        that you have had 1% of all the advertising impressions for that search
                        term.
                    </div>
                    <div className="mt-1 mb-3">
                        A low impression share can generally be interpreted as a search term that
                        has potential to scale. The lower the impression share, the higher the
                        potential scaling can be. You can generally scale a search term either by
                        increasing budgets on items that target that search term or by adding
                        additional products that target that search term.
                    </div>
                </div>
            </div>
            <AgGridTable
                name="KeywordRecommendations"
                showHeader={false}
                tableHooks={rankedTableHooks}
                store={store}
                savedFiltersStore={savedFiltersStore}
                search
                searchPlaceholder="Search by keyword"
                columns={columns}
                dateSelect={false}
                columnSelection
                showPerPage={false}
            />
        </>
    ) : (
        <EmptyState title="No ranking keywords" icon="search">
            Amazon didn't return any ranking keywords for your asin
        </EmptyState>
    )
})

const RecommendedKeywords = ({ loading, unranked }) => {
    if (loading) {
        return null
    }

    return (
        <>
            <div className="mt-10">
                <div className="font-normal text-lg">Recommended Keywords</div>
                <div className="max-w-screen-md my-3">
                    This is list is generated by Amazon. Some of these keywords may be viable ones
                    to add to your campaigns, others may be overly broad or simply bad keywords.
                    Give this list a quick skim and remove any that look too broad or nonsensical
                    before adding to your campaigns!
                </div>
            </div>
            {unranked.length > 0 && (
                <>
                    <textarea
                        rows={Math.min(30, unranked.length)}
                        className="w-full rounded shadow-md border p-3"
                        value={unranked.map((i) => i.keyword).join('\n')}
                    />
                </>
            )}
            {unranked.length === 0 && (
                <>
                    <EmptyState title="No recommended keywords" icon="search">
                        Amazon didn't return any recommended keyword for your asin
                    </EmptyState>
                </>
            )}
        </>
    )
}

const isAsinValid = (asin: string) => {
    const pattern = '/[0-9BC][0-9]{2}[0-9A-Z]{7}|[0-9]{9}(X|0-9]/gi)' // Amazon ASIN
    return !(asin != '' && asin.length !== 10 && !asin.match(pattern))
}
